import React from 'react';
import { Box, Typography, Checkbox, FormControlLabel } from '@mui/material';
import {
  sectionStyles,
  typographyStyles,
  checkboxStyles,
} from '../../../modals/unified_config_modals/styles/unifiedConfigStyles';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import type { PvdConfigFieldsReturn } from '@/hooks/pvd_config_hooks/usePvdConfigFields';
import Flat11SensitivityField from './Flat11SensitivityField';
import SmartAdaptiveExpansion from './SmartAdaptiveExpansion';

const CURRENT_CHANNEL_MIN = 1;
const CURRENT_CHANNEL_MAX = 11;

interface Props {
  editor: PvdConfigFieldsReturn;
  currentEnabled: boolean;
  setCurrentEnabled: (v: boolean) => void;
  readonly?: boolean;
  onChipHover?: (sensitivity: number) => void;
  onChipLeave?: () => void;
}

const CurrentSensitivitySection: React.FC<Props> = ({
  editor,
  currentEnabled,
  setCurrentEnabled,
  readonly = false,
  onChipHover,
  onChipLeave,
}) => {
  const { t } = useTranslation();

  const handleHeaderToggle = (next: boolean) => {
    setCurrentEnabled(next);
    if (next) {
      editor.enableAllCurrent();
      editor.setAdaptiveCurrentAllowed(true);
    } else {
      editor.disableAllCurrent();
      editor.setAdaptiveCurrentAllowed(false);
    }
  };

  return (
    <Box
      sx={{
        ...sectionStyles.currentSection,
        minHeight: { md: readonly ? 'auto' : 624 },
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
          <Typography
            variant="h6"
            sx={{ ...typographyStyles.sectionHeading, mr: 2 }}
          >
            {t(StringKeys.CURRENT_SENSITIVITY)}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={currentEnabled}
                onChange={(e) => handleHeaderToggle(e.target.checked)}
                sx={checkboxStyles.sectionToggle(currentEnabled)}
              />
            }
            label={
              <Typography sx={typographyStyles.checkboxLabel(currentEnabled)}>
                {currentEnabled
                  ? t(StringKeys.ENABLED)
                  : t(StringKeys.DISABLED)}
              </Typography>
            }
          />
        </Box>
        <Typography sx={{ ...typographyStyles.description, mb: 0 }}>
          {t(StringKeys.CURRENT_SENSITIVITY_SECTION_DESCRIPTION)}
        </Typography>
      </Box>

      <Flat11SensitivityField
        values={editor.current}
        onToggle={(i) => editor.toggleCurrentChip(i)}
        smartAdaptive={editor.adaptiveCurrentAllowed}
        setSmartAdaptive={editor.setAdaptiveCurrentAllowed}
        onEnableAll={() => editor.enableAllCurrent()}
        onDisableAll={() => {
          editor.disableAllCurrent();
          editor.setAdaptiveCurrentAllowed(false);
        }}
        readonly={readonly}
        onChipHover={onChipHover}
        onChipLeave={onChipLeave}
      />

      {editor.adaptiveCurrentAllowed && !readonly && (
        <SmartAdaptiveExpansion
          channel="current"
          bubbleSizeIsLocked={editor.currentBubbleSizeIsLocked}
          bubbleSizeLockedToValue={editor.currentBubbleSizeLockedToValue}
          setBubbleUserDefined={editor.setCurrentBubbleUserDefined}
          setBubbleSmall={editor.setCurrentBubbleSmall}
          setBubbleLarge={editor.setCurrentBubbleLarge}
          autoAdaptIsLocked={editor.autoAdaptIsLocked}
          autoAdaptLockedToValue={editor.autoAdaptLockedToValue}
          setAutoAdaptUserDefined={editor.setAutoAdaptUserDefined}
          setAutoAdaptOn={editor.setAutoAdaptOn}
          setAutoAdaptOff={editor.setAutoAdaptOff}
          channelMin={CURRENT_CHANNEL_MIN}
          channelMax={CURRENT_CHANNEL_MAX}
          minValue={editor.currentMinSetting}
          maxValue={editor.currentMaxSetting}
          onRangeChange={editor.setCurrentRange}
        />
      )}
    </Box>
  );
};

export default CurrentSensitivitySection;
